import apiClient from './api.ts';
import { ReportPeriod, ReportPeriodInsert, UpdateChildReportPeriod } from '@/types/report_period_schema.ts';
import type {
  ReportingPeriodKind,
  ReportingPeriodTemplate,
  ReportingPeriodTemplateAssignment,
  ReportingPeriodGenerationPeriod,
} from '@/types/reporting_period_template_schema.ts';

export default {
  async getAllReportPeriods(isActive: boolean, page: number, pageSize: number, periodType?: string, academicYearId?: number, isOnlyIncludeParent?: boolean) {
    const params = new URLSearchParams({
      is_active: String(isActive),
      page: String(page),
      offset: String(pageSize),
    });

    if (periodType) {
      params.append('period-type', periodType);
    }
    if (academicYearId !== undefined && !Number.isNaN(academicYearId)) {
      params.append('academic_year_id', String(academicYearId));
    }
    if (isOnlyIncludeParent !== undefined) {
      params.append('is-only-include-parent', String(isOnlyIncludeParent));
    }

    return await apiClient.get(`report-period?${params.toString()}`);
  },
  async getAllReportPeriodTypes(isActive: boolean, academicYearId?: number) {
    const params = new URLSearchParams({
      is_active: String(isActive),
    });

    if (academicYearId !== undefined && !Number.isNaN(academicYearId)) {
      params.append('academic_year_id', String(academicYearId));
    }

    return await apiClient.get(`report-period/type?${params.toString()}`);
  },
  async getReportPeriodById(id: number) {
    return await apiClient.get(`report-period/id/${id}`);
  },
  async getChildrenPeriodById(id: number) {
    return await apiClient.get(`report-period/${id}/sub-report-period`);
  },
  async createReportPeriod(reportPeriod: ReportPeriodInsert) {
    return await apiClient.post(`report-period`, reportPeriod);
  },
  async updateReportPeriod(reportPeriodId: number, reportPeriod: { period_type?: string; period_value?: number | string; display_name?: string; name?: string; year?: number; academic_year_id?: string; is_included_in_parent?: boolean; score_weight_base_unit?: number; start_date?: string; end_date?: string }) {
    return await apiClient.put(`report-period/${reportPeriodId}`, reportPeriod);
  },
  async updateChildReportPeriod(parentReportPeriodId: number, periodType: string, update: UpdateChildReportPeriod) {
    return await apiClient.put(`report-period/${parentReportPeriodId}/period-type/${periodType}/update`, update);
  },
  async deleteReportPeriod(reportPeriodId: number) {
    return await apiClient.delete(`report-period/${reportPeriodId}`);
  },
  async archiveReportPeriod(reportPeriodId: number) {
    return await apiClient.put(`report-period/${reportPeriodId}/archive`);
  },
  async unarchiveReportPeriod(reportPeriodId: number) {
    return await apiClient.put(`report-period/${reportPeriodId}/unarchive`);
  },

  async getReportingPeriodKinds(isActive = true) {
    return await apiClient.get<{ data: ReportingPeriodKind[] }>(`report-period/kinds?is_active=${isActive}`);
  },

  async getReportingPeriodTemplates(schoolTypeCode?: string, isActive = true) {
    const params = new URLSearchParams({ is_active: String(isActive) });
    if (schoolTypeCode) params.set('school_type_code', schoolTypeCode);
    return await apiClient.get<{ data: ReportingPeriodTemplate[] }>(`report-period/templates?${params.toString()}`);
  },

  async getReportingPeriodTemplate(templateId: number) {
    return await apiClient.get<{ data: ReportingPeriodTemplate }>(`report-period/templates/${templateId}`);
  },

  async getReportingPeriodTemplateAssignment(academicYearId: number) {
    return await apiClient.get<{ data: ReportingPeriodTemplateAssignment }>(`academic-years/${academicYearId}/reporting-period-template`);
  },

  async assignReportingPeriodTemplate(academicYearId: number, templateId: number) {
    return await apiClient.post<{ data: ReportingPeriodTemplateAssignment }>(`academic-years/${academicYearId}/reporting-period-template`, {
      template_id: templateId,
    });
  },

  async generateReportingPeriodsFromTemplate(academicYearId: number, templateId: number, periods: ReportingPeriodGenerationPeriod[]) {
    return await apiClient.post<{ data: { count: number; reporting_periods: ReportPeriod[] } }>(`academic-years/${academicYearId}/reporting-periods/generate`, {
      template_id: templateId,
      periods,
    });
  }
};
